/**
 * useReordering.ts - Unified hook for all reordering operations in the canvas
 *
 * Handles single-node moves, drag-to-reorder, and multi-node group drag operations.
 *
 * Related:
 *   - utils/nodeReordering.ts - Single-node reorder operations
 *   - utils/groupReordering.ts - Group reorder operations
 *   - utils/graphHelpers.ts - Shared graph traversal
 *   - components/AutomationFlowCanvas.tsx - Integration point
 */

import { useCallback, useState } from 'react';
import type { Edge, Node } from '@xyflow/react';
import type * as Api from '@ViewModels';
import { toast } from 'sonner';
import { NODE_TYPES, type BaseNodeData } from '../types';
import type { AutomationStepNodeData, SegmentNodeData } from '../types/stepNodes';
import { resolveBranchCaseTemplateId } from '../utils/branchNodes';
import type { CrossSegmentMoveParams } from '../types/canvasProps';
import type { SelectionGroup, GroupDragState, GroupDropZone } from '../types/selectionGroup';
import type { HistoryActionType } from './useUndoRedo';
import {
	moveNodeAfter,
	moveNodeBefore,
	moveStepUp,
	moveStepDown,
	selectBranch,
	getInsertionPoint,
	type InsertionPoint,
} from '../utils/nodeReordering';
import { moveGroupAfter, moveGroupBefore, moveGroupToBranch } from '../utils/groupReordering';
import { NODE_WIDTH, NODE_MIN_HEIGHT } from '../utils/layoutConstants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Drag state for single-node reorder operations */
export interface DragState {
	isDragging: boolean;
	draggedNodeIds: string[];
	insertionPoint: InsertionPoint | null;
}

/** Distance threshold for valid drop zones */
const DROP_ZONE_THRESHOLD = 500;

const INITIAL_DRAG_STATE: DragState = {
	isDragging: false,
	draggedNodeIds: [],
	insertionPoint: null,
};

const INITIAL_GROUP_STATE: GroupDragState = {
	isDragging: false,
	group: null,
	dropZone: null,
	mousePosition: null,
	dragOffset: { x: 0, y: 0 },
};

/** Options for the useReordering hook */
export interface UseReorderingOptions {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
	takeSnapshot: (actionType: HistoryActionType, description: string) => void;
	/** Callback when a reorder is completed — receives ordered step IDs and template ID */
	onReorderComplete?: (orderedStepIds: string[], templateId: string) => void;
	/** Callback when steps are moved between segments */
	onMoveStepsCrossSegment?: (params: CrossSegmentMoveParams) => Promise<void>;
}

/** Return type for the useReordering hook */
export interface UseReorderingReturn {
	// -- Single node --
	dragState: DragState;
	moveAfter: (nodeId: string, targetNodeId: string) => boolean;
	moveBefore: (nodeId: string, targetNodeId: string) => boolean;
	moveUp: (nodeId: string) => boolean;
	moveDown: (nodeId: string) => boolean;
	selectBranchNodes: (startNodeId: string) => string[];
	startDrag: (nodeIds: string[]) => void;
	updateDragPosition: (position: { x: number; y: number }) => void;
	endDrag: () => { success: boolean; message?: string };
	cancelDrag: () => void;

	// -- Group --
	groupDragState: GroupDragState;
	startGroupDrag: (group: SelectionGroup, startPosition: { x: number; y: number }) => void;
	updateGroupDrag: (position: { x: number; y: number }) => void;
	endGroupDrag: () => { success: boolean; message?: string };
	cancelGroupDrag: () => void;
	isValidDropZone: () => boolean;
}

// ---------------------------------------------------------------------------
// Shared: extract ordered step IDs for a template from result nodes
// ---------------------------------------------------------------------------

function extractOrderedStepIds(
	resultNodes: Node<BaseNodeData>[],
	nodeId: string
): { orderedStepIds: string[]; templateId: string } | null {
	const movedNode = resultNodes.find(n => n.id === nodeId);
	const movedData = movedNode?.data as BaseNodeData & { automationTemplate?: { id?: string }; stepId?: string };
	const movedTemplateId = movedData?.automationTemplate?.id;

	if (!movedTemplateId) {
		return null;
	}

	const orderedStepIds = resultNodes
		.filter(n => {
			const data = n.data as BaseNodeData & { stepId?: string; automationTemplate?: { id?: string } };
			return data.stepId !== undefined && data.automationTemplate?.id === movedTemplateId;
		})
		.sort((a, b) => a.position.y - b.position.y)
		.map(n => (n.data as BaseNodeData & { stepId?: string }).stepId as string);

	return orderedStepIds.length > 0 ? { orderedStepIds, templateId: movedTemplateId } : null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Unified reordering hook for the automation flow canvas.
 *
 * Combines single-node operations (move up/down, drag single node) and
 * group operations (drag selected group, cross-segment move) into one hook.
 */
export function useReordering({
	nodes,
	edges,
	setNodes,
	setEdges,
	takeSnapshot,
	onReorderComplete,
	onMoveStepsCrossSegment,
}: UseReorderingOptions): UseReorderingReturn {
	const [dragState, setDragState] = useState<DragState>(INITIAL_DRAG_STATE);
	const [groupDragState, setGroupDragState] = useState<GroupDragState>(INITIAL_GROUP_STATE);

	// =======================================================================
	// Single-node operations
	// =======================================================================

	/**
	 * Persist the reorder to the server and revert the optimistic update on failure.
	 * Called after setNodes/setEdges have already applied the new graph locally.
	 */
	const persistReorder = useCallback(
		(resultNodes: Node<BaseNodeData>[], nodeId: string, prevNodes: Node<BaseNodeData>[], prevEdges: Edge[]) => {
			if (!onReorderComplete) {
				return;
			}
			const info = extractOrderedStepIds(resultNodes, nodeId);
			if (!info) {
				return;
			}
			Promise.resolve(onReorderComplete(info.orderedStepIds, info.templateId)).catch(() => {
				// API failed — revert the optimistic graph update so the canvas
				// matches the server state. The mutation's onError callback shows
				// the error toast and triggers a refetch.
				setNodes(prevNodes);
				setEdges(prevEdges);
			});
		},
		[onReorderComplete, setNodes, setEdges]
	);

	const moveAfter = useCallback(
		(nodeId: string, targetNodeId: string): boolean => {
			takeSnapshot('batch', 'Move node');
			const result = moveNodeAfter(nodes, edges, nodeId, targetNodeId);
			if (result.success) {
				setNodes(result.nodes);
				setEdges(result.edges);
				persistReorder(result.nodes, nodeId, nodes, edges);
				return true;
			}
			toast.error(result.message || 'Could not move node');
			return false;
		},
		[nodes, edges, setNodes, setEdges, takeSnapshot, persistReorder]
	);

	const moveBefore = useCallback(
		(nodeId: string, targetNodeId: string): boolean => {
			takeSnapshot('batch', 'Move node');
			const result = moveNodeBefore(nodes, edges, nodeId, targetNodeId);
			if (result.success) {
				setNodes(result.nodes);
				setEdges(result.edges);
				persistReorder(result.nodes, nodeId, nodes, edges);
				return true;
			}
			toast.error(result.message || 'Could not move node');
			return false;
		},
		[nodes, edges, setNodes, setEdges, takeSnapshot, persistReorder]
	);

	const applyMoveAndNotify = useCallback(
		(
			result: { nodes: Node<BaseNodeData>[]; edges: Edge[]; success: boolean; message?: string },
			nodeId: string,
			_label: string
		): boolean => {
			if (result.success) {
				setNodes(result.nodes);
				setEdges(result.edges);
				persistReorder(result.nodes, nodeId, nodes, edges);
				return true;
			}
			toast.error(result.message || 'Could not move step');
			return false;
		},
		[nodes, edges, setNodes, setEdges, persistReorder]
	);

	const moveUp = useCallback(
		(nodeId: string): boolean => {
			takeSnapshot('batch', 'Move step up');
			return applyMoveAndNotify(moveStepUp(nodes, edges, nodeId), nodeId, 'Step moved up');
		},
		[nodes, edges, takeSnapshot, applyMoveAndNotify]
	);

	const moveDown = useCallback(
		(nodeId: string): boolean => {
			takeSnapshot('batch', 'Move step down');
			return applyMoveAndNotify(moveStepDown(nodes, edges, nodeId), nodeId, 'Step moved down');
		},
		[nodes, edges, takeSnapshot, applyMoveAndNotify]
	);

	const selectBranchNodes = useCallback(
		(startNodeId: string): string[] => {
			return selectBranch(startNodeId, edges);
		},
		[edges]
	);

	// -- Single-node drag --

	const startDrag = useCallback((nodeIds: string[]) => {
		setDragState({ isDragging: true, draggedNodeIds: nodeIds, insertionPoint: null });
	}, []);

	const updateDragPosition = useCallback(
		(position: { x: number; y: number }) => {
			if (!dragState.isDragging) {
				return;
			}
			const insertionPoint = getInsertionPoint(position, nodes, dragState.draggedNodeIds);
			setDragState(prev => ({ ...prev, insertionPoint }));
		},
		[nodes, dragState.isDragging, dragState.draggedNodeIds]
	);

	const endDrag = useCallback((): { success: boolean; message?: string } => {
		if (!dragState.isDragging || !dragState.insertionPoint) {
			setDragState(INITIAL_DRAG_STATE);
			return { success: false, message: 'No valid drag operation' };
		}
		const { type, targetNodeId } = dragState.insertionPoint;
		if (type === 'invalid' || !targetNodeId) {
			setDragState(INITIAL_DRAG_STATE);
			return { success: false, message: 'Invalid drop position' };
		}
		if (dragState.draggedNodeIds.length !== 1) {
			setDragState(INITIAL_DRAG_STATE);
			return { success: false, message: 'Can only move one node at a time' };
		}

		const nodeId = dragState.draggedNodeIds[0];
		takeSnapshot('batch', 'Move node by drag');

		const result =
			type === 'after'
				? moveNodeAfter(nodes, edges, nodeId, targetNodeId)
				: moveNodeBefore(nodes, edges, nodeId, targetNodeId);

		if (result.success) {
			setNodes(result.nodes);
			setEdges(result.edges);
			persistReorder(result.nodes, nodeId, nodes, edges);
		}

		setDragState(INITIAL_DRAG_STATE);
		return { success: result.success, message: result.message };
	}, [dragState, nodes, edges, setNodes, setEdges, takeSnapshot, persistReorder]);

	const cancelDrag = useCallback(() => {
		setDragState(INITIAL_DRAG_STATE);
	}, []);

	// =======================================================================
	// Group operations
	// =======================================================================

	const calculateDropZone = useCallback(
		(position: { x: number; y: number }, group: SelectionGroup): GroupDropZone => {
			let closestNode: Node<BaseNodeData> | null = null;
			let closestDistance = Infinity;

			for (const node of nodes) {
				if (group.nodeIds.includes(node.id)) {
					continue;
				}
				if (node.type === NODE_TYPES.TRIGGER) {
					continue;
				}
				const nodeWidth = node.measured?.width ?? NODE_WIDTH;
				const nodeHeight = node.measured?.height ?? NODE_MIN_HEIGHT;
				const nodeCenterX = node.position.x + nodeWidth / 2;
				const nodeCenterY = node.position.y + nodeHeight / 2;
				const distance = Math.sqrt(Math.pow(position.x - nodeCenterX, 2) + Math.pow(position.y - nodeCenterY, 2));
				if (distance < closestDistance) {
					closestDistance = distance;
					closestNode = node;
				}
			}

			if (!closestNode || closestDistance > DROP_ZONE_THRESHOLD) {
				return { type: 'invalid', targetNodeId: null, indicatorPosition: position };
			}

			const closestNodeHeight = closestNode.measured?.height ?? NODE_MIN_HEIGHT;
			const closestNodeCenterY = closestNode.position.y + closestNodeHeight / 2;
			const type = position.y < closestNodeCenterY ? 'before' : 'after';

			const isSwitchNode = closestNode.type === NODE_TYPES.SWITCH_STEP;
			if (isSwitchNode && type === 'after') {
				const branchInfo = detectBranchDropZone(closestNode, position, edges);
				if (branchInfo) {
					return {
						type: 'branch',
						targetNodeId: closestNode.id,
						branchInfo,
						indicatorPosition: {
							x: closestNode.position.x + (closestNode.measured?.width ?? NODE_WIDTH) / 2,
							y: closestNode.position.y + (closestNode.measured?.height ?? NODE_MIN_HEIGHT) + 40,
						},
					};
				}
			}

			const indicatorY =
				type === 'before' ? closestNode.position.y - 20 : closestNode.position.y + closestNodeHeight + 20;

			return {
				type,
				targetNodeId: closestNode.id,
				indicatorPosition: {
					x: closestNode.position.x + (closestNode.measured?.width ?? NODE_WIDTH) / 2,
					y: indicatorY,
				},
			};
		},
		[nodes, edges]
	);

	const getTargetTemplateId = useCallback(
		(dropZone: GroupDropZone): string | undefined => {
			if (!dropZone.targetNodeId) {
				return undefined;
			}
			if (dropZone.type === 'branch' && dropZone.branchInfo) {
				return resolveBranchCaseTemplateId(nodes, {
					parentSwitchNodeId: dropZone.branchInfo.switchNodeId,
					caseIndex: dropZone.branchInfo.caseIndex,
					caseId: dropZone.branchInfo.caseId,
				});
			}
			const targetNode = nodes.find(n => n.id === dropZone.targetNodeId);
			if (targetNode) {
				if (targetNode.type === NODE_TYPES.SEGMENT || targetNode.type === NODE_TYPES.BEHAVIOR_CASE) {
					return (targetNode.data as SegmentNodeData).childTemplateId;
				}
				return (targetNode.data as BaseNodeData & { automationTemplate?: { id?: string } }).automationTemplate?.id;
			}
			return undefined;
		},
		[nodes]
	);

	const startGroupDrag = useCallback(
		(group: SelectionGroup, startPosition: { x: number; y: number }) => {
			takeSnapshot('batch', `Move ${group.nodeIds.length} nodes`);
			setGroupDragState({
				isDragging: true,
				group,
				dropZone: null,
				mousePosition: startPosition,
				dragOffset: {
					x: startPosition.x - group.bounds.x,
					y: startPosition.y - group.bounds.y,
				},
			});
		},
		[takeSnapshot]
	);

	const updateGroupDrag = useCallback(
		(position: { x: number; y: number }) => {
			if (!groupDragState.isDragging || !groupDragState.group) {
				return;
			}
			const dropZone = calculateDropZone(position, groupDragState.group);
			setGroupDragState(prev => {
				// Skip update when the drop zone target and type haven't changed.
				// This avoids re-renders on every mouse move when the cursor stays
				// over the same node region.
				if (
					prev.dropZone?.type === dropZone.type &&
					prev.dropZone?.targetNodeId === dropZone.targetNodeId &&
					prev.dropZone?.branchInfo?.caseIndex === dropZone.branchInfo?.caseIndex
				) {
					return prev;
				}
				return { ...prev, mousePosition: position, dropZone };
			});
		},
		[groupDragState.isDragging, groupDragState.group, calculateDropZone]
	);

	const endGroupDrag = useCallback((): { success: boolean; message?: string } => {
		if (!groupDragState.isDragging || !groupDragState.group) {
			setGroupDragState(INITIAL_GROUP_STATE);
			return { success: false, message: 'No active drag operation' };
		}

		const { dropZone, group } = groupDragState;

		if (!dropZone || dropZone.type === 'invalid' || !dropZone.targetNodeId) {
			setGroupDragState(INITIAL_GROUP_STATE);
			return { success: false, message: 'Invalid drop position' };
		}

		// Determine source/target template IDs for cross-segment detection
		const firstGroupNode = nodes.find(n => group.nodeIds.includes(n.id));
		const sourceData = firstGroupNode?.data as AutomationStepNodeData | undefined;
		const sourceTemplateId = sourceData?.automationTemplate?.id;
		const targetTemplateId = getTargetTemplateId(dropZone);

		const isCrossSegmentMove =
			sourceTemplateId && targetTemplateId && sourceTemplateId !== targetTemplateId && onMoveStepsCrossSegment;

		if (isCrossSegmentMove) {
			const groupNodes = nodes.filter(n => group.nodeIds.includes(n.id));
			const allSameTemplate = groupNodes.every(n => {
				return (n.data as AutomationStepNodeData).automationTemplate?.id === sourceTemplateId;
			});
			if (!allSameTemplate) {
				toast.error('Cannot move steps from different templates at once');
				setGroupDragState(INITIAL_GROUP_STATE);
				return { success: false, message: 'Group spans multiple templates' };
			}

			const steps: CrossSegmentMoveParams['steps'] = groupNodes
				.filter(n => {
					const d = n.data as AutomationStepNodeData;
					return d.stepId && d.stepType && d.automationStep;
				})
				.map(n => {
					const d = n.data as AutomationStepNodeData;
					return {
						stepId: d.stepId!,
						stepType: d.stepType as Api.AutomationStepType,
						automationStep: d.automationStep!,
					};
				});

			if (steps.length === 0) {
				toast.error('No saved steps to move');
				setGroupDragState(INITIAL_GROUP_STATE);
				return { success: false, message: 'No saved steps in group' };
			}

			onMoveStepsCrossSegment({ steps, sourceTemplateId, targetTemplateId });
			setGroupDragState(INITIAL_GROUP_STATE);
			return { success: true, message: 'Cross-segment move initiated' };
		}

		// Same-segment reorder
		let result;
		switch (dropZone.type) {
			case 'after':
				result = moveGroupAfter(nodes, edges, group, dropZone.targetNodeId);
				break;
			case 'before':
				result = moveGroupBefore(nodes, edges, group, dropZone.targetNodeId);
				break;
			case 'branch':
				result = dropZone.branchInfo
					? moveGroupToBranch(nodes, edges, group, dropZone.branchInfo.switchNodeId, dropZone.branchInfo.caseIndex)
					: { success: false, message: 'Missing branch info', nodes, edges };
				break;
			default:
				result = { success: false, message: 'Unknown drop type', nodes, edges };
		}

		if (result.success) {
			setNodes(result.nodes as Node<BaseNodeData>[]);
			setEdges(result.edges as Edge[]);

			if (onReorderComplete && sourceTemplateId) {
				const orderedStepIds = (result.nodes as Node<BaseNodeData>[])
					.filter(n => {
						const d = n.data as BaseNodeData & { stepId?: string; automationTemplate?: { id?: string } };
						return d.stepId !== undefined && d.automationTemplate?.id === sourceTemplateId;
					})
					.sort((a, b) => a.position.y - b.position.y)
					.map(n => (n.data as BaseNodeData & { stepId?: string }).stepId as string);
				if (orderedStepIds.length > 0) {
					Promise.resolve(onReorderComplete(orderedStepIds, sourceTemplateId)).catch(() => {
						// API failed — revert the optimistic update. The mutation's
						// onError callback handles the error toast and refetch.
						setNodes(nodes as Node<BaseNodeData>[]);
						setEdges(edges as Edge[]);
					});
				}
			}
		} else {
			toast.error(result.message || 'Could not move nodes');
		}

		setGroupDragState(INITIAL_GROUP_STATE);
		return { success: result.success, message: result.message };
	}, [
		groupDragState,
		nodes,
		edges,
		setNodes,
		setEdges,
		onReorderComplete,
		onMoveStepsCrossSegment,
		getTargetTemplateId,
	]);

	const cancelGroupDrag = useCallback(() => {
		setGroupDragState(INITIAL_GROUP_STATE);
	}, []);

	const isValidDropZone = useCallback(() => {
		return groupDragState.isDragging && groupDragState.dropZone !== null && groupDragState.dropZone.type !== 'invalid';
	}, [groupDragState]);

	return {
		// Single node
		dragState,
		moveAfter,
		moveBefore,
		moveUp,
		moveDown,
		selectBranchNodes,
		startDrag,
		updateDragPosition,
		endDrag,
		cancelDrag,
		// Group
		groupDragState,
		startGroupDrag,
		updateGroupDrag,
		endGroupDrag,
		cancelGroupDrag,
		isValidDropZone,
	};
}

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

function detectBranchDropZone(
	switchNode: Node<BaseNodeData>,
	position: { x: number; y: number },
	edges: Edge[]
): { switchNodeId: string; caseIndex: number; caseId?: string } | null {
	const branchEdges = edges.filter(e => e.source === switchNode.id && e.type === 'branch');
	if (branchEdges.length === 0) {
		return null;
	}
	const nodeWidth = switchNode.measured?.width ?? NODE_WIDTH;
	const zoneWidth = nodeWidth / branchEdges.length;
	const relativeX = position.x - switchNode.position.x;
	const zoneIndex = Math.floor(relativeX / zoneWidth);
	const caseIndex = Math.max(0, Math.min(zoneIndex, branchEdges.length - 1));
	const targetEdge = branchEdges.find(e => e.data?.caseIndex === caseIndex);
	if (targetEdge) {
		const edgeData = targetEdge.data as { caseIndex?: number; caseId?: string } | undefined;
		return {
			switchNodeId: switchNode.id,
			caseIndex: edgeData?.caseIndex as number,
			caseId: edgeData?.caseId,
		};
	}
	return null;
}
